import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";

export default function LoginPage() {
    const { user, login, error } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    if (user && user !== false) return <Navigate to="/dashboard" replace />;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const ok = await login(email, password);
        setSubmitting(false);
        if (ok) navigate("/dashboard");
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-white" data-testid="login-page">
            {/* Left: brand panel */}
            <div className="hidden lg:flex flex-col justify-between p-10 bg-[#0f172a] text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-30 pointer-events-none"
                     style={{ backgroundImage: "url(https://images.unsplash.com/photo-1765046255462-198d49d07dd1?crop=entropy&cs=srgb&fm=jpg&q=70&w=1400)", backgroundSize: "cover", backgroundPosition: "center" }} />
                <div className="relative">
                    <div className="flex items-center gap-3 mb-12">
                        <div className="w-9 h-9 bg-[#002FA7] flex items-center justify-center text-white font-bold">T</div>
                        <div>
                            <div className="text-lg font-semibold tracking-tight">TelecomERP</div>
                            <div className="text-[10px] uppercase tracking-widest text-slate-400">Inter-company Suite</div>
                        </div>
                    </div>
                    <h1 className="text-4xl font-semibold leading-tight max-w-md text-white">
                        Multi-entity accounting for the global telecom stack.
                    </h1>
                    <p className="text-slate-300 mt-4 max-w-md text-[14px] leading-relaxed">
                        Voice and SMS reporting in dual base currencies. CSV-driven sales invoice posting with
                        verify-before-post controls. Real-time receivables, P&amp;L and bank position across five entities.
                    </p>
                </div>
                <div className="relative grid grid-cols-5 gap-3 text-[10px] uppercase tracking-widest text-slate-400">
                    <div>Canada</div>
                    <div>Singapore</div>
                    <div>Malaysia</div>
                    <div>Hong Kong</div>
                    <div>Dubai</div>
                </div>
            </div>

            {/* Right: form */}
            <div className="flex items-center justify-center p-6 lg:p-10">
                <form onSubmit={handleSubmit} className="w-full max-w-sm" data-testid="login-form">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Sign in</div>
                    <h2 className="text-2xl font-semibold tracking-tight mb-1">Welcome back</h2>
                    <p className="text-slate-500 text-[13px] mb-8">Use your administrator credentials to access the workspace.</p>

                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="email" className="text-[11px] uppercase tracking-widest text-slate-600">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                required
                                onChange={(e) => setEmail(e.target.value)}
                                className="rounded-sm h-10 mt-1.5"
                                data-testid="login-email-input"
                                placeholder="admin@telecomerp.com"
                            />
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-[11px] uppercase tracking-widest text-slate-600">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                value={password}
                                required
                                onChange={(e) => setPassword(e.target.value)}
                                className="rounded-sm h-10 mt-1.5"
                                data-testid="login-password-input"
                            />
                        </div>
                        {error && (
                            <div className="text-[12.5px] text-red-600 border border-red-200 bg-red-50 px-3 py-2 rounded-sm" data-testid="login-error">
                                {error}
                            </div>
                        )}
                        <Button
                            type="submit"
                            disabled={submitting}
                            className="w-full h-10 rounded-sm bg-[#002FA7] hover:bg-[#001f6f] text-white"
                            data-testid="login-submit-btn"
                        >
                            {submitting ? "Signing in…" : "Sign in"}
                        </Button>
                    </div>

                    <div className="mt-10 text-[11px] text-slate-500 leading-relaxed border-t border-slate-100 pt-4">
                        <span className="kbd">admin@telecomerp.com</span>
                        <span className="ml-2">/</span>
                        <span className="ml-2 kbd">Admin@2026</span>
                        <div className="mt-2 text-slate-400">Default seeded administrator (change after first login).</div>
                    </div>
                </form>
            </div>
        </div>
    );
}
